import { useMemo } from 'react'
import { RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FilterPill } from '@/components/data-table'
import type {
  QuickFilterState,
  PresenceFilter,
  RelationshipFilter,
  CrmStageFilter,
  FilterPillOption,
} from '@/types/aircrafts'
import {
  PRESENCE_OPTIONS,
  RELATIONSHIP_OPTIONS,
  CRM_STAGE_OPTIONS,
} from '@/types/aircrafts'

interface QuickFilterPillsProps {
  quickFilters: QuickFilterState
  onPresenceChange: (values: PresenceFilter[]) => void
  onRelationshipChange: (values: RelationshipFilter[]) => void
  onStageChange: (values: CrmStageFilter[]) => void
  onLocationChange: (values: string[]) => void
  /** Unique location names from data for the Location pill */
  locationOptions: string[]
  isModified: boolean
  onReset: () => void
}

export function QuickFilterPills({
  quickFilters,
  onPresenceChange,
  onRelationshipChange,
  onStageChange,
  onLocationChange,
  locationOptions,
  isModified,
  onReset,
}: QuickFilterPillsProps) {
  // Build location pill options dynamically from data
  const locationPillOptions = useMemo(
    (): FilterPillOption<string>[] =>
      locationOptions.map((name) => ({
        value: name,
        label: name,
      })),
    [locationOptions]
  )

  return (
    <>
      {locationPillOptions.length > 1 && (
        <FilterPill
          label="Base"
          options={locationPillOptions}
          selected={quickFilters.location}
          onChange={onLocationChange}
        />
      )}
      <FilterPill
        label="Presence"
        options={PRESENCE_OPTIONS}
        selected={quickFilters.presence}
        onChange={(v) => onPresenceChange(v as PresenceFilter[])}
      />
      <FilterPill
        label="Relationship"
        options={RELATIONSHIP_OPTIONS}
        selected={quickFilters.relationship}
        onChange={(v) => onRelationshipChange(v as RelationshipFilter[])}
      />
      <FilterPill
        label="Stage"
        options={CRM_STAGE_OPTIONS}
        selected={quickFilters.stage}
        onChange={(v) => onStageChange(v as CrmStageFilter[])}
      />
      {isModified && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-muted-foreground hover:text-foreground"
          onClick={onReset}
        >
          <RotateCcw className="mr-1 h-3 w-3" />
          Reset
        </Button>
      )}
    </>
  )
}
